/**
 * signContract.ts — reading what the activation RPC said, and where to put it.
 *
 * `sign_contract` REFUSES IN TWO DIALECTS AND THROWS IN A THIRD, and the old screen collapsed
 * all of them into one toast that said "Failed to start contract". The three are:
 *
 *   { success: false, errors: […] }   one sentence per unmet gate — the common case
 *   { success: false, error: '…' }    a single sentence: access denied, contract not found,
 *                                     contract is already IN_PROGRESS
 *   a thrown PostgREST/transport error, which carries no dialect at all
 *
 * Every SENTENCE the server produces is kept VERBATIM. The gate sentences are the only place
 * the server explains itself, and paraphrasing them ("some steps are incomplete") throws away
 * the only thing an admin can act on. What this module adds is the address: which manifest row
 * a sentence belongs to, so a refusal lands on the row that owns it instead of vanishing from
 * a toast four seconds later.
 *
 * A CODE IS NOT A SENTENCE, AND IS NEVER SHOWN AS ONE. From `20260804010000` the refusals arrive as
 * `AGREEMENT_WITNESS_MISSING` rather than as prose, so this module also owns the words: the
 * readiness payload's message for that code where the page has it, `BLOCKER_COPY` where it does
 * not. Verbatim applies to what the server WROTE, never to what it ENUMERATED.
 *
 * PURE ON PURPOSE — vitest runs node-env over `.ts` only, and this is the file whose rules must
 * never drift from the server's wording.
 */

import type { SetupArea } from './steps';
import type { ManifestRowId } from './setupState';

/** What the RPC answers with, in whichever dialect it chose. */
export type SignContractOutcome = 'activated' | 'gates' | 'refusal' | 'error';

export interface SignContractResult {
    outcome: SignContractOutcome;
    /** True only for `{ success: true }`. Nothing else activates a contract. */
    ok: boolean;
    /** One verbatim sentence per unmet gate. Empty unless `outcome === 'gates'`. */
    reasons: string[];
    /** The single verbatim sentence, for the refusal and error dialects. */
    message: string | null;
}

/** What a transport failure with no sentence of its own still has to say. */
export const SIGN_CONTRACT_UNKNOWN_ERROR =
    'The contract could not be activated, and the server gave no reason.';

function sentenceOf(value: unknown): string | null {
    if (typeof value === 'string') return value.trim() || null;
    if (value && typeof value === 'object' && 'message' in value) {
        const message = (value as { message?: unknown }).message;
        if (typeof message === 'string') return message.trim() || null;
    }
    return null;
}

/**
 * The RPC's answer, in one shape.
 *
 * A THROWN ERROR IS NOT A REFUSAL. `sign_contract` returns its refusals as data — a throw means
 * the call itself did not complete (RLS, network, a 500), so it can never be attributed to a
 * gate and is reported as its own sentence.
 */
export function parseSignContractResult(input: unknown): SignContractResult {
    if (input instanceof Error) {
        return {
            outcome: 'error',
            ok: false,
            reasons: [],
            message: sentenceOf(input) ?? SIGN_CONTRACT_UNKNOWN_ERROR,
        };
    }

    if (!input || typeof input !== 'object') {
        return { outcome: 'error', ok: false, reasons: [], message: SIGN_CONTRACT_UNKNOWN_ERROR };
    }

    const body = input as { success?: unknown; errors?: unknown; error?: unknown; message?: unknown };

    if (body.success === true) {
        return { outcome: 'activated', ok: true, reasons: [], message: null };
    }

    const reasons = Array.isArray(body.errors)
        ? body.errors.map((reason) => sentenceOf(reason)).filter((reason): reason is string => !!reason)
        : [];
    if (reasons.length > 0) {
        return { outcome: 'gates', ok: false, reasons, message: null };
    }

    const singular = sentenceOf(body.error);
    if (singular) {
        return { outcome: 'refusal', ok: false, reasons: [], message: singular };
    }

    // A `{ success: false }` with nothing in it, or a thrown PostgREST object that reached us as
    // data: neither names a gate, so neither may be attributed to one.
    return {
        outcome: 'error',
        ok: false,
        reasons: [],
        message: sentenceOf(body.message) ?? SIGN_CONTRACT_UNKNOWN_ERROR,
    };
}

// ─── Where a sentence belongs ────────────────────────────────────────────────

/**
 * `get_preparation_status` item → the manifest row that clears it.
 *
 * THE SIX ITEMS DO NOT ALL LIVE ON THE VEHICLE. `insurance` is a `get_preparation_status` row
 * that reads the contract-scoped insurance junction, so its refusal belongs on the insurance
 * area — sending it to the vehicle (which is where the other five live) would route an admin to
 * a screen that cannot fix it.
 */
const PREP_ITEM_ROWS: Record<string, ManifestRowId> = {
    mileage: 'veh-odometer',
    service: 'veh-service',
    registration: 'veh-registration',
    roadworthiness: 'veh-roadworthiness',
    tracker: 'veh-tracker',
    insurance: 'ins-policy',
};

/** The sentences `sign_contract` appends outside the preparation loop, verbatim. */
const LITERAL_REASON_ROWS: Record<string, ManifestRowId> = {
    'driver agreement not complete': 'agr-hpa',
    'witness agreement not complete': 'agr-witness',
    // Retired in the current body, kept because a contract activated through an older
    // deployment can still surface it and a sentence with no home is a sentence with no Fix link.
    'insurance loan agreement not complete': 'ins-loan-agreement',
    'no insurance loan found': 'ins-loan',
};

const PREP_PREFIX = 'preparation item not complete:';

/**
 * EVERY CODE `get_contract_setup_status` CAN EMIT → THE ROW THAT CLEARS IT.
 *
 * THIS FILE ANSWERS THE SAME QUESTION IN TWO VOCABULARIES ON PURPOSE, AND THAT IS THE POINT.
 * `sign_contract` refuses in ENGLISH SENTENCES today; `20260802030000` makes it consume
 * `get_contract_setup_status`, after which it refuses in CODES. A mapping that understood only one
 * of the two would go blind on the day that migration lands — every refusal arriving with no row
 * to sit on, and the Fix links silently gone from a screen that still looked right. Both dialects
 * are read here, so the migration is a no-op for this client rather than a regression, and the
 * sentences stay for the contracts activated through an older deployment.
 *
 * TOTAL OVER THE FUNCTION'S ARMS. Every code carries `enforced_by: "server"` and means it, so a
 * code with no row is a refusal an admin is shown with nowhere to go. Several codes share a row
 * where the canvas puts the work in one block: the premium and its discount, the loan and its
 * repayment schedule, and the two states of the policy row.
 *
 * `CONTRACT_NOT_DRAFT` IS DELIBERATELY ABSENT. It is filed under `"section": "summary"` — the
 * activation panel's anchor, not a section — so it belongs to the gate and never to a row.
 */
export const BLOCKER_ROWS: Record<string, ManifestRowId> = {
    VEHICLE_NOT_ASSIGNED: 'veh-assign',
    // A DOCUMENT REFUSAL THAT IS NOT ABOUT A DOCUMENT. No instrument prints the odometer reading —
    // the v1 Schedule's Vehicle particulars stop at the condition — so this code is the server
    // holding activation for an operational record, the handover baseline a later argument about
    // wear, mileage or valuation is settled against. The row it lands on is the same either way.
    VEHICLE_MILEAGE_MISSING: 'veh-odometer',
    VEHICLE_SERVICE_MISSING: 'veh-service',
    VEHICLE_REGISTRATION_MISSING: 'veh-registration',
    VEHICLE_ROADWORTHINESS_MISSING: 'veh-roadworthiness',
    VEHICLE_TRACKER_MISSING: 'veh-tracker',

    DRIVER_NOT_ASSIGNED: 'drv-name',
    DRIVER_PROFILE_INCOMPLETE: 'drv-contact',
    DRIVER_REPAYMENT_TAG_MISSING: 'drv-file',
    WITNESS_NOT_ASSIGNED: 'agr-parties',

    TERMS_SALE_PRICE_MISSING: 'fin-price',
    TERMS_INSTALMENT_MISSING: 'fin-instalment',

    // RETIRED BY [D-B41] AND KEPT MAPPED — the current body cannot emit it, an older deployment
    // still can, and a refusal with no home is a refusal with no Fix link.
    DEPOSIT_MISSING: 'dep-payment',
    // [D-B15]. Two codes, one row, and the row is NOT `dep-payment`: the money is recorded and the
    // paper over it is not, so a refusal about the paper belongs on the paper's own line.
    AGREEMENT_DEPOSIT_MISSING: 'dep-agreement',
    AGREEMENT_DEPOSIT_AWAITING_SEAL: 'dep-agreement',

    INSURANCE_NOT_LINKED: 'ins-policy',
    INSURANCE_POLICY_INCOMPLETE: 'ins-policy',
    INSURANCE_PREMIUM_MISSING: 'ins-premium',
    INSURANCE_DISCOUNT_MISSING: 'ins-premium',
    INSURANCE_LOAN_MISSING: 'ins-loan',
    INSURANCE_LOAN_SCHEDULE_MISSING: 'ins-loan',

    AGREEMENT_DRIVER_MISSING: 'agr-hpa',
    AGREEMENT_DRIVER_AWAITING_SEAL: 'agr-hpa',
    AGREEMENT_WITNESS_MISSING: 'agr-witness',
    AGREEMENT_WITNESS_AWAITING_SEAL: 'agr-witness',
    AGREEMENT_INSURANCE_LOAN_MISSING: 'ins-loan-agreement',
    AGREEMENT_INSURANCE_LOAN_AWAITING_SEAL: 'ins-loan-agreement',
    // [D-B48], AND MAPPED BY CODE RATHER THAN BY POSITION. `get_contract_setup_status` appends the
    // partner arm AFTER the three agreement arms that were there before it, so the fourth entry in
    // the `blockers` array is the partner's on a third-party contract and the lifecycle arm's on a
    // house one — a reader that counted would put `AGREEMENT_PARTNER_MISSING`'s sentence on the
    // wrong row for half the fleet. This map is the only thing that decides where a code lands, and
    // it is keyed on the code.
    AGREEMENT_PARTNER_MISSING: 'agr-partner',
    AGREEMENT_PARTNER_AWAITING_SEAL: 'agr-partner',
};

/** The lifecycle arm. It belongs to the activation panel, never to a row. */
export const CONTRACT_NOT_DRAFT = 'CONTRACT_NOT_DRAFT';

/**
 * The one arm the CLIENT can word better than the server can.
 *
 * Named here, beside the rest of the vocabulary, because it is a code and codes live in this file
 * — `setupState` matches on it to swap the server's fixed four-field sentence for the fields that
 * are actually absent. See `spokenBlockers` there and `driverProfileGapSentence` in `@/lib`.
 */
export const DRIVER_PROFILE_INCOMPLETE = 'DRIVER_PROFILE_INCOMPLETE';

/**
 * The arm [D-B41] retired, named because a retired code is read MORE carefully than a live one.
 *
 * `setupState.depositPresent` matches on it to tell an old server's "no deposit, and I refuse over
 * it" apart from a new server's "no deposit, and I do not care" — the two answer the same
 * arithmetic and only the code separates them. It is named here rather than spelled inline there
 * for the reason the whole vocabulary is: this file is where a code the server may stop sending is
 * changed exactly once.
 */
export const DEPOSIT_MISSING = 'DEPOSIT_MISSING';

/**
 * EVERY CODE, IN ENGLISH — THE FALLBACK, NEVER THE FIRST ANSWER.
 *
 * A CODE IS NOT A SENTENCE. `20260804010000` makes `sign_contract` refuse in codes, and a code
 * relayed as-is puts `AGREEMENT_INSURANCE_LOAN_MISSING` on a row note, in the narrator and in the
 * footer — three surfaces reading like a stack trace, on the one screen where the whole point is
 * that an admin can act on what it says.
 *
 * THE SERVER'S OWN WORDING STILL WINS WHERE THERE IS ANY. `get_contract_setup_status` ships a
 * message with every blocker, and the readiness payload is on the page already, so the resolution
 * order is: the payload's sentence for that code, then this map, then the raw code. This map is
 * what answers when the two calls disagree — a refusal for a code the payload is no longer holding
 * (it was re-read after the refusal, and the row has since moved), or a page that refused before
 * the payload arrived. So the sentences here are the SAME CLAIM as the server's, said shorter; they
 * are not a second vocabulary, and they must never say something the server would not.
 *
 * `CONTRACT_NOT_DRAFT` IS HERE THOUGH IT IS ABSENT FROM `BLOCKER_ROWS`, and that is not an
 * inconsistency: that map answers "which row", which the lifecycle arm has none of, and this one
 * answers "in what words", which every code has. It is the one code whose sentence is deliberately
 * vaguer than the server's — the payload names the status it moved to, and this file cannot know it.
 */
export const BLOCKER_COPY: Record<string, string> = {
    VEHICLE_NOT_ASSIGNED: 'No vehicle is assigned to this contract.',
    VEHICLE_MILEAGE_MISSING: 'The vehicle’s starting mileage is not recorded.',
    VEHICLE_SERVICE_MISSING: 'The conversion or servicing job is not recorded.',
    VEHICLE_REGISTRATION_MISSING: 'The vehicle registration is not on file.',
    VEHICLE_ROADWORTHINESS_MISSING: 'No roadworthiness certificate is on file.',
    VEHICLE_TRACKER_MISSING: 'No GPS tracker is installed on the vehicle.',

    DRIVER_NOT_ASSIGNED: 'No driver is assigned to this contract.',
    DRIVER_PROFILE_INCOMPLETE: 'The driver’s profile is incomplete.',
    DRIVER_REPAYMENT_TAG_MISSING: 'The driver has no repayment tag.',
    WITNESS_NOT_ASSIGNED: 'No witness is named on this contract.',

    TERMS_SALE_PRICE_MISSING: 'The vehicle sale price is not set.',
    TERMS_INSTALMENT_MISSING: 'The weekly instalment is not set.',

    // RETIRED BY [D-B41], KEPT FOR THE SAME WINDOW `BLOCKER_ROWS` KEEPS ITS ENTRY FOR.
    DEPOSIT_MISSING: 'No deposit payment is recorded on this contract.',
    // EVERY deposit, not the deposit: the server grades each linked row, so the sentence has to
    // stay true on the contract that took two payments and papered one of them.
    AGREEMENT_DEPOSIT_MISSING: 'A deposit on this contract has no signed agreement.',
    AGREEMENT_DEPOSIT_AWAITING_SEAL: 'The executed deposit agreement is not filed yet.',

    INSURANCE_NOT_LINKED: 'No insurance policy is attached to this contract.',
    INSURANCE_POLICY_INCOMPLETE: 'The policy has no valuation or no start date.',
    INSURANCE_PREMIUM_MISSING: 'The insurance premium payment is not recorded.',
    INSURANCE_DISCOUNT_MISSING: 'The premium discount is not recorded.',
    INSURANCE_LOAN_MISSING: 'The advance that finances the premium does not exist.',
    // NAMED FOR THE MONEY AND NOT FOR THE INSTRUMENT [D-B45]. `get_contract_setup_status` treats a
    // fully-deposited loan's schedule as satisfied, so this arm only ever fires over a premium that
    // really is financed — but the sentence sits in a map an admin reads whole, beside two that
    // name the paperwork, and the paperwork now has two names.
    INSURANCE_LOAN_SCHEDULE_MISSING: 'The financed premium has no repayment schedule.',

    AGREEMENT_DRIVER_MISSING: 'The driver’s hire-purchase agreement is not signed.',
    AGREEMENT_DRIVER_AWAITING_SEAL: 'The executed driver’s agreement is not filed yet.',
    AGREEMENT_WITNESS_MISSING: 'The witness agreement is not signed.',
    AGREEMENT_WITNESS_AWAITING_SEAL: 'The executed witness agreement is not filed yet.',
    // INSTRUMENT-NEUTRAL, AND THE CODE IS UNCHANGED [D-B45]. `sign_contract` raises one arm for the
    // insurance-loan arc whichever of its two published instruments papers the year — an Insurance
    // Premium Advance where Primelane financed the premium, an Insurance Premium Payment Record
    // where the driver paid it in full — and a sentence naming only the advance would send an admin
    // looking for a document this contract does not carry. The row it lands on names the instrument
    // exactly (`labelOf`); this sentence says what is wrong.
    AGREEMENT_INSURANCE_LOAN_MISSING: 'The insurance agreement for this year is not signed.',
    AGREEMENT_INSURANCE_LOAN_AWAITING_SEAL:
        'The executed insurance agreement is not filed yet.',

    // NAMED FOR THE OWNER OF THE CAR, NOT FOR THE PAPERWORK [D-B48]. The row above it is called
    // "Vehicle schedule", so a sentence repeating the document's name would say nothing the row has
    // not; what an admin reading this list needs is the fact that puts the row there at all — this
    // contract is on somebody else's vehicle. The server's own wording still wins where the payload
    // is on the page; this is the shorter restatement the map's rule calls for.
    AGREEMENT_PARTNER_MISSING: 'The vehicle’s partner has not signed a vehicle schedule for it.',
    AGREEMENT_PARTNER_AWAITING_SEAL: 'The executed vehicle schedule is not filed yet.',

    CONTRACT_NOT_DRAFT: 'This contract is no longer a draft.',
};

/**
 * Is this string one of the server's codes rather than one of its sentences?
 *
 * A CODE IS SHOUTED AND HAS NO SPACES, which is what tells the two dialects apart without a flag
 * the caller has to remember to set. No sentence `sign_contract` produces is a single
 * SCREAMING_SNAKE token, and no code contains a space — so the test cannot claim a sentence.
 */
function isBlockerCode(text: string): boolean {
    return /^[A-Z][A-Z0-9_]*$/.test(text);
}

/**
 * The manifest row a refusal belongs to, or null.
 *
 * NULL IS A REAL ANSWER. "Contract is already IN_PROGRESS" and "Access denied" describe the
 * call, not a row, and the rule for those is stated once: show the sentence verbatim with no
 * Fix link, rather than pointing an admin at a row that will not change anything.
 */
export function reasonToRow(reason: string): ManifestRowId | null {
    const raw = reason.trim();
    if (!raw) return null;

    // THE CODE DIALECT FIRST, because it is the one the server is moving to. An unrecognized code
    // falls through to null exactly as an unrecognized sentence does — a refusal shown verbatim
    // with no Fix link, which is honest, rather than pointed at a row picked by accident.
    if (isBlockerCode(raw)) return BLOCKER_ROWS[raw] ?? null;

    const text = raw.toLowerCase();
    if (text.startsWith(PREP_PREFIX)) {
        const item = text.slice(PREP_PREFIX.length).trim();
        return PREP_ITEM_ROWS[item] ?? null;
    }

    return LITERAL_REASON_ROWS[text] ?? null;
}

/** The area a refusal belongs to — the row's area, or null when it belongs to no row. */
export function reasonToStep(reason: string): SetupArea | null {
    const row = reasonToRow(reason);
    if (!row) return null;
    return ROW_AREAS[row];
}

/**
 * Row → area, for the rows a refusal can name.
 *
 * Deliberately a map of its own rather than a lookup into the manifest definition: this file is
 * imported by the manifest, so reaching back into it for the area would make the pair circular
 * at runtime for the sake of six entries that cannot move independently anyway.
 */
const ROW_AREAS: Record<ManifestRowId, SetupArea> = {
    'dep-payment': 'deposit',
    'dep-agreement': 'deposit',
    'veh-assign': 'vehicle',
    'veh-service': 'vehicle',
    'veh-registration': 'vehicle',
    'veh-roadworthiness': 'vehicle',
    'veh-tracker': 'vehicle',
    'veh-odometer': 'vehicle',
    'drv-name': 'driver',
    'drv-contact': 'driver',
    'drv-file': 'driver',
    'ins-policy': 'insurance',
    'ins-premium': 'insurance',
    'ins-loan': 'insurance',
    'ins-loan-agreement': 'insurance',
    'fin-cash-price': 'financials',
    'fin-price': 'financials',
    'fin-instalment': 'financials',
    'agr-parties': 'agreements',
    'agr-partner': 'agreements',
    'agr-hpa': 'agreements',
    'agr-witness': 'agreements',
};

export interface LandedRefusal {
    /**
     * The sentence to show, ready to read. A refusal that arrived AS a sentence is that sentence
     * verbatim; a refusal that arrived as a CODE is the payload's wording for it, or this file's,
     * and only a code neither of them knows is shown raw.
     */
    message: string;
    /** The row it lands on, or null when it belongs to the call rather than to a row. */
    rowId: ManifestRowId | null;
    area: SetupArea | null;
}

/**
 * ONE REFUSAL, IN WORDS. The three-step order, and nothing reads a code past this point.
 *
 * The sentence dialect never enters the ladder — `isBlockerCode` is what keeps the two apart, and a
 * sentence the server worded is already the best wording there is.
 */
function refusalText(reason: string, payloadMessages?: ReadonlyMap<string, string>): string {
    if (!isBlockerCode(reason)) return reason;
    const fromPayload = payloadMessages?.get(reason)?.trim();
    if (fromPayload) return fromPayload;
    return BLOCKER_COPY[reason] ?? reason;
}

/**
 * Every refusal with the row it lands on. Order is the server's evaluation order.
 *
 * `payloadMessages` is `get_contract_setup_status`'s code → message map, and passing it is what
 * makes the refusal and the readiness payload say the same thing about the same code. It is
 * OPTIONAL because it is genuinely absent on a page that refused before the payload arrived — and
 * that page still shows English, out of `BLOCKER_COPY`.
 */
export function landRefusals(
    reasons: string[],
    payloadMessages?: ReadonlyMap<string, string>,
): LandedRefusal[] {
    return reasons
        .map((reason) => reason.trim())
        .filter(Boolean)
        .map((reason) => {
            const rowId = reasonToRow(reason);
            return {
                message: refusalText(reason, payloadMessages),
                rowId,
                area: rowId ? ROW_AREAS[rowId] : null,
            };
        });
}
